import React, { useMemo } from 'react';

import { Box, MenuItem, Select, TextField, FormControl, InputLabel } from '@mui/material';
import { closeModal } from 'components/Modal/modalReducer';
import ModalWrapper from 'components/Modal/ModalWrapper';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as actions from 'store/actions';
import useStyles from 'styles/forms';
import Autocomplete from '@mui/material/Autocomplete';
import { unitStatusOptions } from 'constants/index';

const getForm = (unit) => ({
  name: unit?.unitname || '',
  floor: unit?.floor || '',
  gla: unit?.gla || '',
  weightedFactor: unit?.weightedFactor ?? 1,
  unitid: unit?.unitid || '',
  building: unit?.buildingname || '',
  comment: unit?.comment || '',
  status: unit?.status || 'under_management'
});

const AddUnit = () => {
  const classes = useStyles();
  const unit = useSelector((state) => state.modals.modalProps.unit);
  const [form, setForm] = React.useState(getForm(unit));
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.tenants.loading);
  const { data: asset } = useSelector((state) => state.assets.assetDetails);
  const { unitSchedule } = useSelector((state) => state.assets.unit);

  const assetNames = useMemo(
    () =>
      unitSchedule.reduce((acc, item) => {
        if (item.buildingname?.trim()?.length > 0 && !acc.includes(item.buildingname)) {
          acc.push(item.buildingname);
        }

        return acc;
      }, []),
    [unitSchedule]
  );

  const handleSave = () => {
    if (!form.name || !form.floor || !form.gla) {
      return toast.error('Please fill in the form!');
    }

    const schedule = unitSchedule.find(it => it.buildingname === form.building);

    const updatedForm = {
      ...form,
      autonomous_building: schedule?.autonomousBuilding ?? false,
      establishment_of_horizontal_property: schedule?.establishmentOfHorizontalProperty ?? false,
    };

    return dispatch(actions.createUnit(updatedForm, asset.id))
      .then(() => {
        toast.success('Success');
        setForm(getForm());
        dispatch(closeModal());
        dispatch(actions.getUnitSchedule(asset.id));
        dispatch(actions.getUnitStackingPlan(asset.id));
        dispatch(actions.getTenantStackingPlan(asset.id));
        dispatch(actions.getAssetKpi(asset.id));
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const handleChange = (name, value) => {
    setForm((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <ModalWrapper
      size="xl"
      header={form.unitid ? 'Edit Unit' : 'Create Unit'}
      label={form.unitid ? 'Edit Unit' : 'Create Unit'}
      handleSave={handleSave}
      disabled={isEqual(unit, form)}
      loading={!!loading}
    >
      <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
        <Autocomplete
          freeSolo
          options={assetNames}
          value={form.building}
          onChange={(event, newValue) => {
            handleChange('building', newValue);
          }}
          onInputChange={(event, newInputValue) => {
            handleChange('building', newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Asset Name (Optional)"
            />
          )}
          style={{ width: '400px' }}
        />
        <TextField
          fullWidth
          label="Unit Name"
          variant="outlined"
          className={classes.textField}
          value={form.name}
          disabled={loading}
          onChange={(e) => handleChange('name', e.target.value)}
          id="unit"
          slotProps={{
            input: {
              classes: {
                input: classes.input
              }
            }
          }}
        />
        <TextField
          fullWidth
          label="Floor"
          variant="outlined"
          className={classes.textField}
          value={form.floor}
          disabled={loading}
          onChange={(e) => handleChange('floor', e.target.value)}
          id="Floor"
          slotProps={{
            input: {
              classes: {
                input: classes.input
              }
            }
          }}
        />

        <TextField
          fullWidth
          label="GLA (m2)"
          variant="outlined"
          className={classes.textField}
          value={form.gla}
          disabled={loading}
          onChange={(e) => handleChange('gla', e.target.value)}
          id="GLA"
          slotProps={{
            input: {
              classes: {
                input: classes.input
              }
            }
          }}
        />

        <TextField
          fullWidth
          label="Weighted Factor"
          variant="outlined"
          className={classes.textField}
          value={form.weightedFactor}
          disabled={loading}
          onChange={(e) => handleChange('weightedFactor', e.target.value)}
          id="weightedFactor"
          slotProps={{
            input: {
              classes: {
                input: classes.input
              }
            }
          }}
        />

        <FormControl fullWidth className={classes.textField}>
          <InputLabel>Status</InputLabel>
          <Select
            value={form.status}
            label="Status"
            onChange={(e) => handleChange('status', e.target.value)}
            disabled={loading}
          >
            {unitStatusOptions.map((option) => (
              <MenuItem key={option.id} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Comment"
          variant="outlined"
          className={classes.textField}
          value={form.comment}
          disabled={loading}
          onChange={(e) => handleChange('comment', e.target.value)}
          id="Comment"
          rows={4}
          slotProps={{
            input: {
              classes: {
                input: classes.input
              }
            }
          }}
        />
      </Box>
    </ModalWrapper>
  );
};

export default AddUnit;
